import { ErrorCode, ONE_DAY, ONE_HOUR, ONE_WEEK, } from "@shared";
// How long after the wallets of the canisters a user had before being migrated to a MultiUser
// canister have been checked for funds to check them again. Each check queries each wallet's
// balance on every token, and funds only reach those wallets now if sent from outside OpenChat, so
// once a day is plenty. A move which failed for a passing reason (see `retryFor`) is tried again
// sooner, since that's usually because the old canister was still being closed out or its cycles
// refunded. It isn't tried on every load though, since each attempt which gets as far as a
// transfer costs the LocalUserIndex cycles.
export const PREVIOUS_WALLETS_CHECK_INTERVAL = ONE_DAY;
export const PREVIOUS_WALLETS_RETRY_INTERVAL = ONE_HOUR;
// A balance whose move failed with an error from the LocalUserIndex, eg. because the ledger refuses
// every transfer, fails the same way each time it's tried while it stays the same, so it isn't
// tried again until it changes, or until this long has passed, in case the ledger has recovered
export const PREVIOUS_WALLETS_STUCK_RETRY_INTERVAL = ONE_WEEK;
// Moves anything left in the user's previous wallets to their wallet, if they have any and a check
// is due, returning the outcome on each ledger moved from, or undefined if no check was made. The
// next check is put off before this one starts, so that tabs loading at the same time don't each
// make it, and one which is cut short, or in which the wallets couldn't be checked, is tried again
// later. A stuck balance (see `PREVIOUS_WALLETS_STUCK_RETRY_INTERVAL`) is passed over, and gets no
// outcome.
export async function movePreviousWalletFunds(user, find, move, now = Date.now()) {
    if ((user.previousUserIds ?? []).length === 0)
        return undefined;
    const key = `openchat_previous_wallets_next_check_${user.userId}`;
    const next = nextCheck(key);
    // A check further off than any is put off for was put off by a clock which was ahead
    if (now < next && next <= now + PREVIOUS_WALLETS_CHECK_INTERVAL)
        return undefined;
    setNextCheck(key, now + PREVIOUS_WALLETS_RETRY_INTERVAL);
    let funds;
    try {
        funds = await find();
    }
    catch {
        return undefined;
    }
    // A stuck balance which has since changed, or gone, is forgotten here. So is one whose balance
    // couldn't be read this time, since `find` leaves it out, which costs one more failed move.
    const stuckKey = `openchat_previous_wallets_stuck_${user.userId}`;
    const stillStuck = stuckBalances(stuckKey).filter((s) => s.failedAt <= now &&
        now < s.failedAt + PREVIOUS_WALLETS_STUCK_RETRY_INTERVAL &&
        funds.some((f) => isSameBalance(s, f)));
    const outcomes = await move(funds.filter((f) => !stillStuck.some((s) => isSameBalance(s, f))));
    const newlyStuck = outcomes.flatMap(({ previousUserId, ledger, result }) => {
        if (result.kind !== "failed" || retryFor(result.error) !== "stuck")
            return [];
        const funded = funds.find((f) => f.previousUserId === previousUserId && f.ledger === ledger);
        return funded === undefined
            ? []
            : [{ previousUserId, ledger, balance: funded.balance.toString(), failedAt: now }];
    });
    setStuckBalances(stuckKey, [...stillStuck, ...newlyStuck]);
    const retrySoon = outcomes.some((o) => o.result.kind === "failed" && retryFor(o.result.error) === "soon");
    setNextCheck(key, now + (retrySoon ? PREVIOUS_WALLETS_RETRY_INTERVAL : PREVIOUS_WALLETS_CHECK_INTERVAL));
    return outcomes;
}
function retryFor(error) {
    if (error.code <= ErrorCode.Unknown)
        return "soon";
    switch (error.code) {
        case ErrorCode.AlreadyInProgress:
        case ErrorCode.CyclesBalanceTooLow:
        case ErrorCode.NotInitialized:
            return "soon";
        case ErrorCode.C2CError:
        case ErrorCode.LedgerNotFound:
            return "next_check";
        default:
            return "stuck";
    }
}
function isSameBalance(stuck, funds) {
    return (stuck.previousUserId === funds.previousUserId &&
        stuck.ledger === funds.ledger &&
        stuck.balance === funds.balance.toString());
}
// Without storage, the wallets are checked on every load
function nextCheck(key) {
    try {
        return Number(localStorage.getItem(key) ?? 0) || 0;
    }
    catch {
        return 0;
    }
}
function setNextCheck(key, at) {
    try {
        localStorage.setItem(key, at.toString());
    }
    catch {
        // Checked again on the next load
    }
}
// Without storage, or if what's stored can't be read, nothing is stuck
function stuckBalances(key) {
    try {
        const stored = JSON.parse(localStorage.getItem(key) ?? "[]");
        return Array.isArray(stored) ? stored.filter(isStuckBalance) : [];
    }
    catch {
        return [];
    }
}
function isStuckBalance(value) {
    return (typeof value === "object" &&
        value !== null &&
        "previousUserId" in value &&
        typeof value.previousUserId === "string" &&
        "ledger" in value &&
        typeof value.ledger === "string" &&
        "balance" in value &&
        typeof value.balance === "string" &&
        "failedAt" in value &&
        typeof value.failedAt === "number");
}
function setStuckBalances(key, stuck) {
    try {
        if (stuck.length === 0) {
            localStorage.removeItem(key);
        }
        else {
            localStorage.setItem(key, JSON.stringify(stuck));
        }
    }
    catch {
        // Tried again on the next check
    }
}
